import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import ImageWithFallback from "./ImageWithFallback";
import { Sparkle, ArrowRight, Loader2, Share2, Check } from "lucide-react";

/**
 * Hero card for the rolling "Best Trip in Next N Months" view.
 * Fetches from GET /api/recommendations/best?months={6|12} and renders a
 * confidence badge, best travel window, and CTA to the recommendation detail.
 */
export default function BestTripHero({ months, title }) {
    const [rec, setRec] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");
    const [shared, setShared] = useState(false);

    useEffect(() => {
        let cancel = false;
        setLoading(true); setError("");
        api.get(`/recommendations/best?months=${months}`)
            .then((r) => { if (!cancel) setRec(r.data.recommendation); })
            .catch((e) => { if (!cancel) setError(e.response?.data?.detail || e.message); })
            .finally(() => { if (!cancel) setLoading(false); });
        return () => { cancel = true; };
    }, [months]);

    const share = async () => {
        if (!rec) return;
        const w = rec.window || {};
        const url = `${window.location.origin}/recommendation/${rec.id}?season=this`;
        const text = `My best trip in the next ${months} months is ${rec.city}, ${rec.country} — ⭐ ${w.confidence_score}% astrological confidence · ${w.start} → ${w.end}. Find yours at StarPath. ${url}`;
        try {
            if (typeof navigator !== "undefined" && navigator.share) {
                await navigator.share({ title: `${rec.city} · StarPath`, text, url });
            } else if (navigator.clipboard) {
                await navigator.clipboard.writeText(text);
            }
            setShared(true);
            setTimeout(() => setShared(false), 2000);
        } catch { /* user cancelled share sheet — ignore */ }
    };

    if (loading) {
        return (
            <div className="mt-8 flex items-center justify-center py-24 text-ink-muted" data-testid={`best-trip-${months}-loading`}>
                <Loader2 strokeWidth={1.5} className="w-5 h-5 animate-spin mr-2" /> Scanning the sky ahead…
            </div>
        );
    }
    if (error) return <div className="mt-8 text-red-500 text-sm" data-testid={`best-trip-${months}-error`}>{error}</div>;
    if (!rec) return <div className="mt-8 text-ink-muted text-sm" data-testid={`best-trip-${months}-empty`}>No strong recommendation over this horizon yet — check back soon.</div>;

    const w = rec.window || {};
    const conf = w.confidence_score || 0;

    return (
        <div className="mt-8 grid lg:grid-cols-2 gap-8 items-stretch" data-testid={`best-trip-${months}-hero`}>
            {/* Image side */}
            <Link
                to={`/recommendation/${rec.id}?season=this`}
                className="relative rounded-3xl overflow-hidden group aspect-[4/3] lg:aspect-auto"
                data-testid={`best-trip-${months}-image-link`}
            >
                <ImageWithFallback src={rec.image} alt={rec.city} accentColor={rec.planet_color} imgClassName="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
                <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-black/10 to-transparent" />
                <div className="absolute top-4 left-4 sp-pill bg-black/40 text-white border-white/20 backdrop-blur">
                    <Sparkle strokeWidth={1.25} className="w-3 h-3" /> {rec.planet} · {title}
                </div>
                {rec.image_photographer && (
                    <div className="absolute bottom-2 right-2 text-[9px] tracking-wide text-white/80 bg-black/35 backdrop-blur-sm px-2 py-0.5 rounded-full border border-white/10">
                        © {rec.image_photographer} · Pexels
                    </div>
                )}
            </Link>

            {/* Details side */}
            <div className="flex flex-col justify-between">
                <div>
                    <div className="sp-overline" data-testid={`best-trip-${months}-title`}>{title}</div>
                    <h2 className="font-serif text-4xl sm:text-5xl tracking-tight mt-3 leading-[1.05]" data-testid={`best-trip-${months}-city`}>
                        {rec.city}<span className="text-ink-muted">, {rec.country}</span>
                    </h2>
                    <div className="mt-4 flex flex-wrap items-baseline gap-3">
                        <span className="font-serif text-6xl tabular-nums" data-testid={`best-trip-${months}-score`}>{conf}</span>
                        <span className="text-sm text-ink-muted">Astrological confidence</span>
                    </div>

                    <div className="mt-6 bg-surface border border-ink/10 rounded-2xl p-5" data-testid={`best-trip-${months}-window`}>
                        <div className="sp-overline">Best travel window</div>
                        <div className="mt-2 text-lg tabular-nums">{w.start} → {w.end}</div>
                        <div className="text-xs text-ink-muted mt-1">
                            {w.duration_days} day trip · optimal in {new Date((rec.horizon_month || w.start) + "T00:00:00Z").toLocaleDateString(undefined, { month: "long", year: "numeric" })}
                        </div>
                    </div>

                    <p className="mt-6 text-ink-soft leading-relaxed" data-testid={`best-trip-${months}-explanation`}>
                        {rec.explanation}
                    </p>

                    {w.astrological_reason && (
                        <div className="mt-4 text-sm text-ink-muted italic" data-testid={`best-trip-${months}-reason`}>
                            {w.astrological_reason}
                        </div>
                    )}
                </div>

                <Link
                    to={`/recommendation/${rec.id}?season=this`}
                    className="mt-6 sp-btn-primary self-start inline-flex items-center gap-2"
                    data-testid={`best-trip-${months}-cta`}
                >
                    {months === 6 ? "View Full Recommendation" : "Explore Destination"}
                    <ArrowRight strokeWidth={1.5} className="w-4 h-4" />
                </Link>

                <button
                    type="button"
                    onClick={share}
                    className={`mt-3 self-start inline-flex items-center gap-2 text-xs tracking-wide px-3 py-1.5 rounded-full border transition-colors ${shared ? "border-emerald-500/40 text-emerald-600 bg-emerald-500/5" : "border-ink/15 text-ink-muted hover:text-ink hover:border-ink/40"}`}
                    aria-label="Share this trip"
                    data-testid={`best-trip-${months}-share`}
                >
                    {shared ? (
                        <><Check strokeWidth={1.75} className="w-3.5 h-3.5" /> Copied to clipboard</>
                    ) : (
                        <><Share2 strokeWidth={1.5} className="w-3.5 h-3.5" /> Share this trip</>
                    )}
                </button>
            </div>
        </div>
    );
}
